import { OrderEntryMode } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/types';
import { OrderEntryTableRowData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import {
  selectSalesPortalCartItemUUIDs,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { useEffect, useMemo, useState } from 'react';
import { useSalesPortalOrderEntryReviewDataLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-sales-portal-order-entry-review-data-loader';

const isSameRowArray = (
  a: OrderEntryTableRowData[],
  b: OrderEntryTableRowData[],
) => a.length === b.length && b.every((row, index) => row === a[index]);

/**
 * Rows for the review view: cart items in the order they were added
 *
 * While in Review, rows removed from the cart keep their last snapshot and
 * new cart adds append; leaving Review resets
 *
 * @param mode - Current order entry mode
 * @param guideRows - Build list rows, reused so carted rows keep item identity across mode toggles
 * @returns Review table rows sorted by time added
 */
export const useOrderEntryReviewRows = (
  mode: OrderEntryMode,
  guideRows: OrderEntryTableRowData[],
): OrderEntryTableRowData[] => {
  const { tableData: loaderRows } = useSalesPortalOrderEntryReviewDataLoader();
  const cartItemUUIDsString = useCartStore((state) =>
    selectSalesPortalCartItemUUIDs(state).join(','),
  );

  const liveRows = useMemo(() => {
    const cartUUIDs = cartItemUUIDsString ? cartItemUUIDsString.split(',') : [];
    const guideByKey = new Map(
      guideRows.map((row) => [row.variantPackUUID, row]),
    );
    const loaderByKey = new Map(
      (loaderRows ?? []).map((row) => [row.variantPackUUID, row]),
    );
    const addedAtByKey = new Map(
      (useCartStore.getState().savedCarts[0]?.cartItems ?? []).map(
        (cartItem) => [cartItem.variantPackUUID, cartItem.addedAt],
      ),
    );
    const addedAtOf = (row: OrderEntryTableRowData) =>
      addedAtByKey.get(row.variantPackUUID) ?? row.addedAt ?? '';

    return cartUUIDs
      .map((uuid) => guideByKey.get(uuid) ?? loaderByKey.get(uuid))
      .filter((row): row is OrderEntryTableRowData => row !== undefined)
      .sort((a, b) => addedAtOf(a).localeCompare(addedAtOf(b)));
  }, [cartItemUUIDsString, guideRows, loaderRows]);

  const [rows, setRows] = useState<OrderEntryTableRowData[]>([]);

  useEffect(() => {
    if (mode === OrderEntryMode.Build) {
      setRows((current) =>
        isSameRowArray(current, liveRows) ? current : liveRows,
      );
      return;
    }
    setRows((current) => {
      const liveByKey = new Map(
        liveRows.map((row) => [row.variantPackUUID, row]),
      );
      const refreshed = current.map(
        (row) => liveByKey.get(row.variantPackUUID) ?? row,
      );
      const currentKeys = new Set(current.map((row) => row.variantPackUUID));
      const additions = liveRows.filter(
        (row) => !currentKeys.has(row.variantPackUUID),
      );
      const next =
        additions.length > 0 ? [...refreshed, ...additions] : refreshed;
      return isSameRowArray(current, next) ? current : next;
    });
  }, [liveRows, mode]);

  return rows;
};
